import LoginWithOTPModal from "@/app/components/routes/modals/LoginWithOTPModal";
import GoogleSigninButton from "@/app/components/ui/Buttons/GoogleSigninButton";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import React from "react";
import { ILoginPageProps } from "./interface";

const LoginPage: React.FC<ILoginPageProps> = () => {
  const {
    SCREEN_APPOINTMENT,
    SCREEN_CUSTOMER_ONBOARDING,
    SCREEN_CUSTOMER_SEARCH,
    SCREEN_SLOTS,
    SCREEN_PWL,
  } = useImageConstants();
  const {
    env: { version },
  } = useConfiguration();
  const { setVisible } = useModalActions("LOGIN_WITH_OTP_MODAL");

  const featureScreens = [
    { src: SCREEN_APPOINTMENT, title: "Booking Confirmation" },
    { src: SCREEN_CUSTOMER_ONBOARDING, title: "Customer Onboarding" },
    { src: SCREEN_CUSTOMER_SEARCH, title: "Customer Search" },
    { src: SCREEN_SLOTS, title: "Appointment Bookings" },
    { src: SCREEN_PWL, title: "Patient Waiting List" },
  ];

  const renderFeatureCard = (item: { src: string; title: string }, className = "") => (
    <div className={`relative overflow-hidden rounded-xl bg-gray-900/5 shadow-lg ${className}`}>
      <img src={item.src} alt={item.title} className="aspect-[2/3] w-full object-cover" />
      <p className="absolute top-2 left-2 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] sm:text-xs text-blue-700">
        {item.title}
      </p>
      <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10" />
    </div>
  );

  return (
    <div className="relative min-h-screen bg-white overflow-x-hidden">
      <header className="relative z-20">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8" aria-label="Global">
          <div className="flex">
            <span className="sr-only">Vetic VMS</span>
            <img
              src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/Website-Astro/Footer/LogoandLocation.webp"
              alt="Vetic Logo"
              className="h-8 w-auto object-contain"
            />
          </div>
        </nav>
      </header>
      <main className="relative z-10 pb-8 sm:pb-10">
        <div className="relative isolate">
          <svg
            className="absolute inset-x-0 top-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
                width={200}
                height={200}
                x="50%"
                y={-1}
                patternUnits="userSpaceOnUse"
              >
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <svg x="50%" y={-1} className="overflow-visible fill-gray-50">
              <path
                d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                strokeWidth={0}
              />
            </svg>
            <rect width="100%" height="100%" strokeWidth={0} fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)" />
          </svg>
          <div
            className="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24"
            aria-hidden="true"
          >
            <div
              className="aspect-[801/1036] w-[40rem] sm:w-[50rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30"
              style={{
                clipPath:
                  "polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)",
              }}
            />
          </div>
          <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8">
            <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
              <div className="w-full max-w-2xl text-center lg:text-left">
                <h1 className="text-2xl sm:text-4xl 2xl:text-6xl font-bold tracking-tight text-gray-900">
                  Veterinary Management System
                </h1>
                <p className="mt-4 sm:mt-6 text-sm sm:text-base 2xl:text-lg leading-relaxed text-gray-600">
                  Manage your appointments, patient waiting list and customer profiles in one place.
                </p>
                <div className="mt-3 sm:mt-4">
                  <p className="text-sm text-gray-700">
                    Just shipped <span className="text-blue-600 font-medium">v{version}</span>
                  </p>
                </div>
                <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:justify-start">
                  <button
                    className="bg-black text-white px-4 py-2.5 rounded-md text-sm font-medium w-full sm:w-auto"
                    onClick={() => setVisible()}
                  >
                    Login With OTP
                  </button>
                  <div className="w-full sm:w-auto flex justify-center sm:justify-start max-w-sm mx-auto sm:mx-0">
                    <GoogleSigninButton />
                  </div>
                </div>
              </div>

              <div className="w-full">
                <div className="lg:hidden">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {featureScreens.map((item, idx) => (
                      <div key={idx} className={idx === featureScreens.length - 1 ? "col-span-2 sm:col-span-1" : ""}>
                        {renderFeatureCard(item)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hidden lg:flex justify-end gap-5 xl:gap-6">
                  <div className="w-40 xl:w-44 space-y-5 pt-20">
                    {renderFeatureCard(featureScreens[0])}
                  </div>
                  <div className="w-40 xl:w-44 space-y-5 pt-8">
                    {renderFeatureCard(featureScreens[1])}
                    {renderFeatureCard(featureScreens[2])}
                  </div>
                  <div className="w-40 xl:w-44 space-y-5 pt-16">
                    {renderFeatureCard(featureScreens[3])}
                    {renderFeatureCard(featureScreens[4])}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <LoginWithOTPModal />
    </div>
  );
};

export default LoginPage;
